import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './admin.css';

const AdminEntregas = () => {
  const [ventas, setVentas] = useState([]);
  const [envios, setEnvios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generando, setGenerando] = useState({});

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      const [ventasRes, enviosRes] = await Promise.all([
        api.get('/ventas'),
        api.get('/envios')
      ]);
      
      // Ventas con dirección y sin guía
      const ventasConEnvio = ventasRes.data.filter(v => 
        v.cliente_direccion && !v.guia_estafeta
      );
      
      setVentas(ventasConEnvio);
      setEnvios(enviosRes.data);
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Función para calcular peso estimado basado en el total
  const calcularPeso = (venta) => {
    // Asumiendo que cada $100 pesos equivalen a 1kg aprox
    return Math.max(1, Math.round(venta.total / 100));
  };

  const generarGuia = async (venta) => {
    setGenerando(prev => ({ ...prev, [venta.id]: true }));

    try {
      // Preparar datos para ENVIA con TODOS los campos necesarios
      const data = {
        ventaId: venta.id,
        cp_destino: venta.cliente_cp,
        peso: calcularPeso(venta),
        valor_declarado: venta.total,
        nombre: venta.cliente_nombre,
        telefono: venta.cliente_telefono || '0000000000',
        email: venta.cliente_email || 'cliente@email.com',
        direccion: venta.cliente_direccion,
        ciudad: venta.cliente_ciudad,
        estado: venta.cliente_estado || 'PUE', // Si no hay estado, usar Puebla por defecto
        carrier: 'estafeta',
        service: 'ground', // Terrestre (más económico)
        largo: 30,
        alto: 20,
        ancho: 15
      };

      console.log('📦 Enviando a generar-guia:', data);

      const response = await api.post('/envios/generar-guia', data);

      if (response.data.success) {
        alert(`✅ Guía generada exitosamente:\n📦 Número: ${response.data.data.guia}\n💰 Costo: $${response.data.data.precio}`);
        cargarDatos();
      } else {
        alert('❌ Error: ' + (response.data.error || 'No se pudo generar la guía'));
      }
    } catch (error) {
      console.error('❌ Error detallado:', error);
      alert('❌ Error al generar guía: ' + (error.response?.data?.error || error.message));
    } finally {
      setGenerando(prev => ({ ...prev, [venta.id]: false }));
    }
  };

  const verEtiqueta = (url) => {
    window.open(url, '_blank');
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return 'N/A';
    return new Date(fecha).toLocaleDateString('es-MX');
  };

  if (loading) return <div className="loading">Cargando...</div>;

  return (
    <div className="admin-entregas">
      <div className="page-header">
        <h1>📦 Gestión de Envíos</h1>
        <p>Genera guías para las ventas pendientes de envío</p>
      </div>

      {/* Ventas pendientes */}
      <section className="ventas-section">
        <h2>Ventas pendientes de envío ({ventas.length})</h2>
        <div className="table-responsive">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Folio</th>
                <th>Cliente</th>
                <th>Dirección</th>
                <th>Ciudad</th>
                <th>CP</th>
                <th>Total</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {ventas.map(v => (
                <tr key={v.id}>
                  <td className="font-mono">{v.folio}</td>
                  <td>{v.cliente_nombre}</td>
                  <td>{v.cliente_direccion}</td>
                  <td>{v.cliente_ciudad}</td>
                  <td>{v.cliente_cp}</td>
                  <td className="text-right">${v.total}</td>
                  <td>
                    <button 
                      className="btn-primary btn-small"
                      onClick={() => generarGuia(v)}
                      disabled={generando[v.id]}
                    >
                      {generando[v.id] ? 'Generando...' : 'Generar guía'}
                    </button>
                  </td>
                </tr>
              ))}
              {ventas.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center">
                    No hay ventas pendientes de envío
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Envíos generados */}
      <section className="envios-section">
        <h2>Envíos generados ({envios.length})</h2>
        <div className="table-responsive">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Folio</th>
                <th>Cliente</th>
                <th>Guía</th>
                <th>Carrier</th>
                <th>Costo</th>
                <th>Fecha</th>
                <th>Estatus</th>
                <th>Etiqueta</th>
              </tr>
            </thead>
            <tbody>
              {envios.map(e => (
                <tr key={e.id}>
                  <td className="font-mono">{e.folio || 'N/A'}</td>
                  <td>{e.cliente_nombre || 'N/A'}</td>
                  <td className="font-mono">{e.guia}</td>
                  <td>{e.carrier}</td>
                  <td className="text-right">${e.costo}</td>
                  <td>{formatearFecha(e.fecha_envio)}</td>
                  <td>
                    <span className={`status-badge ${e.estatus}`}>
                      {e.estatus}
                    </span>
                  </td>
                  <td>
                    {e.tracking_url ? (
                      <button 
                        className="btn-small"
                        onClick={() => verEtiqueta(e.tracking_url)}
                      >
                        📄 Ver
                      </button>
                    ) : (
                      <span className="pendiente">Sin etiqueta</span>
                    )}
                  </td>
                </tr>
              ))}
              {envios.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center">
                    No hay envíos generados
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default AdminEntregas;